import { useMemo } from "react";

import SprayChartVisualization, {
  CHART_HEIGHT_IN_PX,
  CHART_WIDTH_IN_PX,
} from "@/assets/SprayChartVisualization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

export type SprayChartData = {
  label: string;
  icon: "circle" | "triangle";
  color: string;
  points: { x: number; y: number }[];
};

type SprayChartCardProps = {
  title: string;
  data: SprayChartData[];
  loading?: boolean;
  showLegend?: boolean;
} & React.ComponentProps<typeof Card>;

export const SprayChartCard = ({
  title,
  data = [],
  loading = false,
  showLegend = true,
  ...rest
}: SprayChartCardProps) => {
  const markerSize = 5;

  const allMarkers = useMemo(() => {
    return data.flatMap(({ points, color, icon }, dataIndex) => {
      if (!points) {
        return [];
      }
      return points.map(({ x, y }, coordIndex) => {
        // Clamp the coordinates to ensure they are within the spray chart bounds
        x = Math.max(markerSize, Math.min(x, CHART_WIDTH_IN_PX - markerSize));
        y = Math.max(markerSize, Math.min(y, CHART_HEIGHT_IN_PX - markerSize));

        if (icon === "circle") {
          return (
            <circle
              cx={x}
              cy={y}
              r={markerSize}
              fill={color}
              key={`${dataIndex}-${coordIndex}`}
              role="img"
              data-testid={`${dataIndex}-${coordIndex}`}
            />
          );
        }

        return (
          <polygon
            points={`${x},${y - markerSize} ${x - markerSize},${y + markerSize} ${x + markerSize},${y + markerSize}`}
            fill={color}
            key={`${dataIndex}-${coordIndex}`}
            role="img"
            data-testid={`${dataIndex}-${coordIndex}`}
          />
        );
      });
    });
  }, [data]);

  const showMarkersHorizontally = data.length > 4;

  return (
    <Card {...rest} className={cn("w-full p-1 flex flex-col", rest.className)}>
      <CardHeader className="p-2">
        <CardTitle className="text-xs md:text-sm text-center">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-hidden flex-1">
        <Separator className="mb-2" />
        <div className="w-full h-full relative">
          <SprayChartVisualization>{allMarkers}</SprayChartVisualization>

          {showLegend && (
            <div className="flex relative">
              <div
                className={
                  showMarkersHorizontally
                    ? cn("gap-2 mx-auto my-0")
                    : cn("absolute left-0 bottom-0")
                }
              >
                {data.map(({ label, color, icon }, index) => (
                  <>
                    {index > 0 &&
                      index % 3 === 0 &&
                      showMarkersHorizontally && <hr className="block" />}
                    <div
                      className={cn(
                        showMarkersHorizontally ? "inline-flex" : "flex",
                        `items-center space-x-1 px-2`,
                      )}
                      key={label}
                    >
                      {icon === "circle" && (
                        <span
                          className={cn(
                            `inline-block w-2 h-2 rounded-full bg-black`,
                          )}
                          style={{ backgroundColor: color }}
                          aria-hidden="true"
                          data-testid={`icon-${index}`}
                        />
                      )}
                      {icon === "triangle" && (
                        <span
                          style={{ color: color }}
                          className={cn(`text-xs d-block`)}
                          aria-hidden="true"
                          data-testid={`icon-${index}`}
                        >
                          ▲
                        </span>
                      )}
                      <h3 className="text-xs">{label}</h3>
                    </div>
                  </>
                ))}
              </div>
            </div>
          )}
        </div>
        {loading && (
          <div className="absolute inset-0 bg-white/75 flex items-center justify-center rounded-lg">
            <Spinner className="h-8 w-8" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
